import { CategoryCard, Skeleton } from '@vizit/atoms';
import { cx } from '@vizit/theme';
import type { CategoriesListProps } from './types';

/**
 * CategoriesList displays a list of category cards.
 * Each card is interactive and can trigger a callback when pressed.
 *
 * @example
 * <CategoriesList
 *   categories={[
 *     {
 *       id: '1',
 *       title: 'Food & Grocery',
 *       images: [
 *         { src: '/image1.jpg', alt: 'Product 1' },
 *         { src: '/image2.jpg', alt: 'Product 2' },
 *         { src: '/image3.jpg', alt: 'Product 3' },
 *       ],
 *     },
 *     // ... more categories
 *   ]}
 *   onCategoryPress={(categoryId) => console.log('Selected:', categoryId)}
 * />
 */
function CategoriesList(props: CategoriesListProps) {
  const { categories, onCategoryPress, className, isLoading = false } = props;

  return (
    <ul
      className={cx('grid grid-cols-[repeat(auto-fill,minmax(240px,1fr))] gap-x-5 gap-y-6', className)}
      aria-label="Product categories"
      aria-busy={isLoading}
    >
      {isLoading
        ? Array.from({ length: 8 }).map((_, index) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: safe to use index as key for loading skeletons
            <li key={`skeleton-${index}`} className="flex flex-col gap-3">
              <Skeleton className="aspect-[60/41] w-full rounded-none" />
              <Skeleton className="h-6 w-40 rounded-none" />
            </li>
          ))
        : categories.map((category) => (
            <li key={category.id}>
              <CategoryCard
                title={category.title}
                images={category.images}
                backgroundColor={category.backgroundColor}
                onPress={() => onCategoryPress?.(category.id)}
              />
            </li>
          ))}
    </ul>
  );
}

export { CategoriesList };
